/**
 * TypeName — a type's name, with everything else about it behind it.
 *
 * WHY THE MARK IS NOT ON THE LINE. The schema's icon was tried inline, leading
 * the name and trailing it, at four sizes and three alignments. Both positions
 * interrupt: leading, the mark announces a word the sentence has not reached;
 * trailing, it annotates one the sentence has moved past; and on a thirteen-type
 * ask either way is confetti. That is a property of prose, not of placement — a
 * pictogram between two words stops the reading either side of it.
 *
 * So the line is words, and the mark, the owner and the description sit behind
 * the name: hover, focus, or tap. Three questions this page never answered close
 * together — what the type looks like, whose vocabulary it is, and what it IS,
 * which is the one a person deciding actually has ("what is a percept?").
 *
 * THE DOTTED RULE IS THE WHOLE ADVERTISEMENT. Nothing else says there is more
 * here. It is quieter than the page's links (solid, inked) because following it
 * is optional: every fact behind it qualifies the row, and none of it changes
 * what is being granted.
 *
 * TAP IS NOT AN AFTERTHOUGHT. This page opens on phones — an app redirects to it
 * — so the trigger is a real button: click toggles, Escape closes, the card is
 * `aria-describedby` so a screen reader gets it on focus. Hover is the shortcut,
 * not the mechanism.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Box } from 'lucide-react';

export function TypeName({ face, children }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  // IS THERE MORE, AND ARE WE AT THE END OF IT. A macOS overlay scrollbar is
  // invisible until the wheel moves, which is the unobtrusive half of what was
  // asked for and leaves the other half — that there IS more — unsaid. A fade
  // on the last line says it, and lifting the fade at the end is what keeps it
  // from eating the final words.
  const [more, setMore] = useState(false);
  const ref = useRef(null);
  const closing = useRef(null);
  const id = useId();

  // Measured when it opens, not on every scroll: the card belongs to a word on
  // a page that does not move under it, and a listener per name would be a
  // listener per row.
  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ left: Math.min(r.left, window.innerWidth - 288), top: r.bottom });
  };
  const show = () => { place(); setOpen(true); };
  const gauge = (el) => {
    if (!el) return;
    setMore(el.scrollHeight - el.clientHeight - el.scrollTop > 2);
  };
  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // A tap anywhere else closes it — the phone's version of "mouse out".
    const onAway = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onAway);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onAway);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closing.current), []);

  if (!face) return children;
  const who = face.creator ? `made by ${face.creator}` : face.native ? 'an ofSelf type' : null;

  return (
    <button type="button" ref={ref} className="gp-tname"
            aria-expanded={open} aria-describedby={open ? id : undefined}
            onMouseEnter={show} onMouseLeave={hide}
            onFocus={show} onBlur={hide}
            onPointerDown={(e) => {
              // TOUCH TOGGLES; A MOUSE DOES NOT. On a mouse the pointer has
              // already opened the card by arriving — handling the click as a
              // toggle then closed it on the way in, which reads as the card
              // refusing to open. A finger has no hover to arrive with, so for
              // touch and pen the press IS the mechanism.
              if (e.pointerType === 'mouse') return;
              e.preventDefault();
              if (open) hide(); else show();
            }}
            onClick={(e) => e.preventDefault()}>
      {children}
      {open && at && (
        /* THE GAP IS PART OF THE CARD. The outer span is transparent and
           carries the distance from the word as padding, so the pointer never
           crosses anything that is not this control; only the inner one is
           drawn. Nothing about the spacing changes — it moved from a
           coordinate into a box. */
        <span className="gp-tcard" id={id} role="tooltip"
              style={{ left: `${at.left}px`, top: `${at.top}px` }}
              onMouseEnter={show} onMouseLeave={hide}>
          <span className="gp-tcard__body">
          <span className="gp-tcard__top">
            {face.icon && (
              <DynamicIcon name={face.icon} size={15} strokeWidth={1.5}
                           className="gp-tcard__icon" aria-hidden="true"
                           fallback={() => <Box size={15} strokeWidth={1.5}
                                                className="gp-tcard__icon" aria-hidden="true" />} />
            )}
            <span className="gp-tcard__name">{children}</span>
            {who && <span className="gp-tcard__who">{who}</span>}
          </span>
          {face.description
            ? (
              <span className={`gp-tcard__desc${more ? ' has-more' : ''}`}
                    ref={gauge} onScroll={(e) => gauge(e.currentTarget)}>
                {face.description}
              </span>
            )
            : <span className="gp-tcard__none">No description for this type.</span>}
          </span>
        </span>
      )}
    </button>
  );
}

export default TypeName;
